import React, {RefObject, useCallback, useEffect, useMemo, useState} from 'react';
import {Button, Collapse, List, ListItemSecondaryAction, ListItemText} from '@mui/material';
import IconButton from '@mui/material/IconButton';
import {TrashCan16} from '@carbon/icons-react';
import {DragDropContext, Draggable, DraggableProvidedDraggableProps, DropResult, Droppable} from '@hello-pangea/dnd';
import {defaultSubmitButtonProps} from '../../Buttons/SubmitButton';
import {createPortal, flushSync} from 'react-dom';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ListItem from '../../materialui/ListItem/ListItem';
import {getInputDefinitionCategories} from './utils';
import {ExecutionSetupType} from 'hemwb-api';

type InputListItem = {
  title: string;
  id: number;
  categoryId: string;
  valid: boolean;
};

type InputListProps = {
  subModelType: ExecutionSetupType;
  definitions: InputListItem[];
  setSelectedInputId: (id?: number) => void;
  selectedInputId?: number;
  setSelectedCategoryId: (id?: string) => void;
  onInputOrderChange: (originalIndex: number, newIndex: number, categoryId: string) => void;
  onInputAdd: () => void;
  onInputDelete: (inputId: number) => void;
  inputListPortalRef?: RefObject<HTMLDivElement>;
};

const getItemStyle = (isDragging: boolean, draggableStyle: DraggableProvidedDraggableProps['style']) => ({
  // styles we need to apply on draggables
  ...draggableStyle,

  ...(isDragging && {
    background: 'rgb(235,235,235)',
  }),
});

const InputList: React.FC<InputListProps> = ({
  subModelType,
  definitions,
  onInputOrderChange,
  selectedInputId,
  setSelectedInputId,
  setSelectedCategoryId,
  onInputAdd,
  onInputDelete,
  inputListPortalRef,
}) => {
  const inputCategories = getInputDefinitionCategories(subModelType);

  const indexById = useMemo(() => {
    return definitions.reduce((acc, definition, index) => {
      acc[definition.id] = index;
      return acc;
    }, {} as any);
  }, [definitions]);

  const [openCategories, setOpenCategories] = useState<string[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const toggleOpenCategory = useCallback(
    (categoryId: string) => {
      if (openCategories.includes(categoryId)) {
        setOpenCategories(openCategories.filter((cId) => cId !== categoryId));
      } else {
        setOpenCategories([...openCategories, categoryId]);
      }
      setSelectedCategoryId(categoryId);
    },
    [openCategories, setSelectedCategoryId],
  );

  const openCategory = useCallback((categoryId: string) => {
    setOpenCategories((c) => [...c.filter((cId) => cId !== categoryId), categoryId]);
  }, []);

  useEffect(() => {
    if (selectedInputId) {
      const categoryId = definitions.find((d: any) => d.id === selectedInputId)?.categoryId;
      if (categoryId) {
        openCategory(categoryId);
        setSelectedCategoryId(categoryId);
      }
    }
  }, [definitions, selectedInputId, openCategory, setSelectedCategoryId]);

  const setSelectedValues = useCallback(
    (id: number, categoryId: string) => {
      setSelectedInputId(id);
      setSelectedCategoryId(categoryId);
    },
    [setSelectedInputId, setSelectedCategoryId],
  );

  const onDragEnd = useCallback(
    (result: DropResult, _provided: any) => {
      setIsDragging(false);
      if (!result.destination) {
        return;
      }

      // @hello-pangea/dnd v18 requires 0-based sequential indices per Droppable, so
      // Draggable uses category-relative indices. Convert back to global indices here
      // because handleInputOrderChange (in InputDefinition.tsx) expects global indices.
      const draggableId = parseInt(result.draggableId);
      const globalOriginalIndex = indexById[draggableId];
      const destCategoryId = result.destination.droppableId;
      const destCategoryRelIndex = result.destination.index;

      // Check if this is a cross-category drop
      const sourceCategoryId = definitions[globalOriginalIndex]?.categoryId;
      const isCrossCategory = sourceCategoryId !== destCategoryId;

      let globalDestIndex: number;
      if (isCrossCategory) {
        // For cross-category drops, always pass newIndex=0 so handleInputOrderChange
        // uses its special branch that calculates the correct global insertion point
        // based on category ordering — the only branch that handles category changes correctly.
        globalDestIndex = 0;
        // Ensure the destination category is open so the moved item is visible after drop.
        openCategory(destCategoryId);
      } else {
        // Same-category reorder: map category-relative index back to global index
        const destCategoryItems = definitions.filter((d) => d.categoryId === destCategoryId);
        if (destCategoryRelIndex >= destCategoryItems.length) {
          globalDestIndex = indexById[destCategoryItems[destCategoryItems.length - 1].id] + 1;
        } else {
          globalDestIndex = indexById[destCategoryItems[destCategoryRelIndex].id];
        }
      }

      onInputOrderChange(globalOriginalIndex, globalDestIndex, destCategoryId);
    },
    [onInputOrderChange, indexById, definitions, openCategory],
  );

  const onBeforeDragStart = useCallback(() => {
    flushSync(() => {
      setIsDragging(true);
    });
  }, []);

  const renderOutput = (
    <div>
      <DragDropContext onDragEnd={onDragEnd} onBeforeDragStart={onBeforeDragStart}>
        {Object.keys(inputCategories).map((categoryId: string) => {
          const definitionsInCategory = definitions.filter((d) => d.categoryId === categoryId);
          const categoryOpen = openCategories.includes(categoryId);

          return (
            <Droppable droppableId={categoryId} key={categoryId}>
              {(droppableProvided, droppableState) => {
                const draggableItems = definitionsInCategory.map((input, categoryIndex) => {
                  const id = input.id;
                  const titleValue = input.title;
                  return (
                    <Draggable key={id} draggableId={String(id)} index={categoryIndex}>
                      {(draggableProvided, snapshot) => (
                        <ListItem
                          component="div"
                          selectedLeftIndicator
                          onClick={() => setSelectedValues(id, categoryId)}
                          selected={id === selectedInputId}
                          ref={draggableProvided.innerRef}
                          {...draggableProvided.draggableProps}
                          {...draggableProvided.dragHandleProps}
                          style={getItemStyle(snapshot.isDragging, draggableProvided.draggableProps.style)}
                        >
                          <ListItemText
                            primary={titleValue || 'EMPTY'}
                            primaryTypographyProps={{color: input.valid ? 'textPrimary' : 'error'}}
                          />
                          <ListItemSecondaryAction>
                            <IconButton edge="end" aria-label="delete" onClick={() => onInputDelete(input.id)}>
                              <TrashCan16 />
                            </IconButton>
                          </ListItemSecondaryAction>
                        </ListItem>
                      )}
                    </Draggable>
                  );
                });

                // innerRef on the outer div so the entire category area (header + items)
                // is the droppable zone. Previously innerRef was only on <List>, so
                // hovering over the category header (above <List>) left the pointer in
                // no droppable zone — causing the cursor to switch to pointer and
                // cross-category drops to fail.
                return (
                  <div
                    ref={droppableProvided.innerRef}
                    {...droppableProvided.droppableProps}
                    style={isDragging ? {minHeight: '4rem'} : undefined}
                  >
                    <ListItem
                      component="button"
                      onClick={definitionsInCategory.length === 0 ? undefined : () => toggleOpenCategory(categoryId)}
                    >
                      <ListItemText
                        secondary={`${inputCategories[categoryId]} (${definitionsInCategory.length})`}
                        secondaryTypographyProps={{
                          color: droppableState.isDraggingOver ? 'primary' : 'textSecondary',
                        }}
                      />
                      {definitionsInCategory.length === 0 ? null : categoryOpen ? (
                        <ExpandLessIcon />
                      ) : (
                        <ExpandMoreIcon />
                      )}
                    </ListItem>
                    <List dense>
                      <Collapse in={categoryOpen} timeout="auto">
                        {draggableItems}
                      </Collapse>
                    </List>
                    {droppableProvided.placeholder}
                  </div>
                );
              }}
            </Droppable>
          );
        })}
      </DragDropContext>
      <br />
      <Button
        {...defaultSubmitButtonProps}
        type="button"
        size="small"
        className=""
        onClick={onInputAdd}
        style={{marginRight: '2rem', float: 'right'}}
      >
        Add
      </Button>
    </div>
  );

  if (inputListPortalRef && inputListPortalRef.current) {
    return createPortal(renderOutput, inputListPortalRef.current);
  }

  return renderOutput;
};

export default InputList;
